import { type CrownEnv } from "./env-config";

/** What picking an env in the picker should do. */
export type EnvSwitchAction = "ignore" | "confirm" | "switch";

/**
 * Envs whose selection is confirmed before it takes effect, because their data
 * is live and customer facing.
 */
const CONFIRMED_ENVS: ReadonlySet<CrownEnv> = new Set<CrownEnv>(["prod"]);

/**
 * Whether selecting this env is confirmed first, so the picker can also mark it
 * as dangerous instead of the two cues drifting apart.
 */
export const needsConfirmation = (env: CrownEnv): boolean =>
  CONFIRMED_ENVS.has(env);

/**
 * Decides what picking `next` should do while `current` is active.
 *
 * Selecting the active env is ignored rather than re-applied: switching reloads
 * the page, so treating a no-op selection as a switch would throw away in-flight
 * form state for nothing.
 *
 * Prod is confirmed because every module reached from the picker writes to the
 * selected env, and switching was a single uncommitted click, so a mis-click
 * while hunting for another env pointed bulk import, config migration and piece
 * backfill at live customer data.
 *
 * @returns `ignore` when nothing should happen, `confirm` when the operator must
 *   agree first, `switch` to apply immediately.
 */
export const envSwitchAction = (
  current: CrownEnv,
  next: CrownEnv,
): EnvSwitchAction => {
  if (next === current) {
    return "ignore";
  }
  return CONFIRMED_ENVS.has(next) ? "confirm" : "switch";
};
